/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useRef, useCallback } from "react";
import toast from "react-hot-toast";
import { Modal, ModalHeader, ModalBody, Button } from "reactstrap";
import { useCurrency } from "../../utils/useCurrency";
import styles from "./FlashEditModal.module.scss";

const MONTHS = [
  { value: "01", label: "ENE" },
  { value: "02", label: "FEB" },
  { value: "03", label: "MAR" },
  { value: "04", label: "ABR" },
  { value: "05", label: "MAY" },
  { value: "06", label: "JUN" },
  { value: "07", label: "JUL" },
  { value: "08", label: "AGO" },
  { value: "09", label: "SEP" },
  { value: "10", label: "OCT" },
  { value: "11", label: "NOV" },
  { value: "12", label: "DIC" },
];

const CURRENT_YEAR = new Date().getFullYear();

const MIN_YEAR = CURRENT_YEAR - 1;
const MAX_YEAR = CURRENT_YEAR + 2;

const MonthYearPicker = ({ month, year, onChange, label, disabled, hideLabel, compact }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayYear, setDisplayYear] = useState(Number(year) || CURRENT_YEAR);
  const ref = useRef(null);

  useEffect(() => {
    setDisplayYear(Number(year) || CURRENT_YEAR);
  }, [year]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const monthLabel = MONTHS.find((m) => m.value === month)?.label || "---";

  return (
    <div className={styles.pickerWrapper} ref={ref}>
      {!hideLabel && <label className={styles.fieldLabel}>{label}</label>}
      <button
        type="button"
        className={styles.pickerToggle}
        style={{
          ...(disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}),
          ...(compact ? { height: "auto", padding: "2px 8px", fontSize: "13px" } : {}),
        }}
        onClick={() => {
          if (disabled) return;
          setIsOpen(!isOpen);
        }}
      >
        {monthLabel} {year}
      </button>
      {isOpen && (
        <div className={styles.pickerDropdown}>
          <div className={styles.yearNav}>
            <button
              type="button"
              className={styles.yearArrow}
              onClick={() => setDisplayYear((y) => Math.max(MIN_YEAR, y - 1))}
              disabled={displayYear <= MIN_YEAR}
            >
              &lsaquo;
            </button>
            <span className={styles.yearLabel}>{displayYear}</span>
            <button
              type="button"
              className={styles.yearArrow}
              onClick={() => setDisplayYear((y) => Math.min(MAX_YEAR, y + 1))}
              disabled={displayYear >= MAX_YEAR}
            >
              &rsaquo;
            </button>
          </div>
          <div className={styles.monthGrid}>
            {MONTHS.map((m) => {
              const isSelected = month === m.value && String(displayYear) === String(year);
              return (
                <button
                  key={m.value}
                  type="button"
                  className={`${styles.monthCell} ${isSelected ? styles.monthSelected : ""}`}
                  onClick={() => {
                    onChange(m.value, String(displayYear));
                    setIsOpen(false);
                  }}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Modal reutilizable para edición rápida (Flash) de oportunidades.
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - opportunity: objeto oportunidad ({ id, estatus, mes_cierre, mes_factura })
 *  - onSave: (updatedFields) => Promise<void>
 *    Recibe { estatus, mes_cierre, mes_factura, ano } listos para Supabase.
 *  - saving: boolean (estado de guardado externo)
 *  - hideFacturacion: boolean (oculta picker facturación, ej. contratos)
 *  - subtitle: string (texto extra en el header, ej. nombre empresa)
 */
export const FlashEditModal = ({ isOpen, onClose, opportunity, onSave, saving, hideFacturacion, subtitle }) => {
  const [estatus, setEstatus] = useState("");
  const [mesCierre, setMesCierre] = useState("01");
  const [anoCierre, setAnoCierre] = useState(String(CURRENT_YEAR));
  const [mesFactura, setMesFactura] = useState("01");
  const [anoFactura, setAnoFactura] = useState(String(CURRENT_YEAR));
  const [cuotas, setCuotas] = useState([]);
  const { formatAmount } = useCurrency();

  const moneda = opportunity?.moneda_dolar === true ? "USD" : opportunity?.pais === "Perú" ? "PEN" : "CLP";

  useEffect(() => {
    if (opportunity) {
      setEstatus(opportunity.estatus || "");
      if (opportunity.mes_cierre) {
        const [mc, ac] = opportunity.mes_cierre.split("-");
        setMesCierre(mc || "01");
        setAnoCierre(ac || String(CURRENT_YEAR));
      }
      if (opportunity.mes_factura) {
        const [mf, af] = opportunity.mes_factura.split("-");
        setMesFactura(mf || "01");
        setAnoFactura(af || String(CURRENT_YEAR));
      }
      if (opportunity.meses && Array.isArray(opportunity.meses)) {
        setCuotas([...opportunity.meses]);
      }
    }
  }, [opportunity]);

  const handleSave = useCallback(async () => {
    if (!estatus) {
      toast.error("Selecciona un estatus.");
      return;
    }
    if (hideFacturacion) {
      const suma = cuotas.reduce((acc, c) => acc + Number(c.importe || 0), 0);
      const total = Number(opportunity?.importe_total || opportunity?.importe || 0);
      if (Math.round(suma) !== Math.round(total)) {
        toast.error(
          `La suma de cuotas ($${suma.toLocaleString("es-CL")}) no coincide con el total del contrato ($${total.toLocaleString("es-CL")})`
        );
        return;
      }
    } else {
      const cierreDate = new Date(`${anoCierre}-${mesCierre}-01`);
      const facturaDate = new Date(`${anoFactura}-${mesFactura}-01`);
      if (cierreDate > facturaDate) {
        toast.error("El cierre debe ser igual o anterior a la facturación.");
        return;
      }
    }
    const updatedFields = {
      estatus,
      mes_cierre: `${mesCierre.padStart(2, "0")}-${anoCierre}`,
      updated_at: new Date().toISOString(),
    };
    if (hideFacturacion) {
      updatedFields.meses = cuotas;
    } else {
      updatedFields.mes_factura = `${mesFactura.padStart(2, "0")}-${anoFactura}`;
      updatedFields.ano = parseInt(anoFactura, 10);
    }
    await onSave(updatedFields);
  }, [estatus, mesCierre, anoCierre, mesFactura, anoFactura, hideFacturacion, cuotas, opportunity, onSave]);

  if (!opportunity) return null;

  const displayName = opportunity.clienteNombre || subtitle || `#${opportunity.id}`;
  const campaignLabel = opportunity.campana || opportunity.code || "";

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered className={styles.flashModal}>
      <ModalHeader toggle={onClose} className={styles.modalHeader}>
        <div className={styles.headerContent}>
          <span className={styles.headerTitle}>{displayName}</span>
          {campaignLabel && <span className={styles.headerSubtitle}>{campaignLabel}</span>}
          <span className={styles.headerTotal}>
            {formatAmount(Number(opportunity?.importe_total || opportunity?.importe || 0), moneda)}
          </span>
        </div>
      </ModalHeader>
      <ModalBody className={styles.modalBody}>
        <div className={styles.formRow}>
          <div className={styles.formCol}>
            <label className={styles.fieldLabel}>Status</label>
            <select className={`form-control ${styles.fieldInput}`} value={estatus} onChange={(e) => setEstatus(e.target.value)}>
              <option value="">Seleccionar</option>
              <option value="100%">100%</option>
              <option value="90%">90%</option>
              <option value="75%">75%</option>
              <option value="50%">50%</option>
              <option value="0%">0%</option>
            </select>
          </div>
          <MonthYearPicker
            label="Cierre"
            month={mesCierre}
            year={anoCierre}
            disabled={estatus === "100%"}
            onChange={(m, y) => {
              setMesCierre(m);
              setAnoCierre(y);
            }}
          />
          {!hideFacturacion && (
            <MonthYearPicker
              label="Facturación"
              month={mesFactura}
              year={anoFactura}
              onChange={(m, y) => {
                setMesFactura(m);
                setAnoFactura(y);
              }}
            />
          )}
        </div>
        {hideFacturacion && cuotas.length > 0 && (
          <div style={{ marginTop: "16px" }}>
            <label
              style={{
                fontSize: "11px",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "#6c757d",
                fontWeight: 500,
              }}
            >
              Calendario de facturación
            </label>
            <table
              className="table table-sm"
              style={{ fontSize: "13px", marginTop: "6px", tableLayout: "fixed", width: "100%" }}
            >
              <thead>
                <tr className={styles.tableHead}>
                  <th style={{ width: "10%" }}>#</th>
                  <th style={{ width: "30%" }}>Mes</th>
                  <th style={{ width: "30%", textAlign: "right" }}>Importe</th>
                  <th style={{ width: "30%" }}>N° factura</th>
                </tr>
              </thead>
              <tbody>
                {cuotas.map((cuota, idx) => {
                  const [cMes, cAno] = (cuota.mes || "").split("-");
                  return (
                    <tr key={idx}>
                      <td style={{ color: "#6c757d", width: "10%" }}>{idx + 1}</td>
                      <td style={{ width: "30%" }}>
                        <div style={{ display: "flex", gap: "4px" }}>
                          <select
                            className="form-control form-control-sm"
                            value={cMes || "01"}
                            onChange={(e) => {
                              const updated = [...cuotas];
                              updated[idx] = {
                                ...updated[idx],
                                mes: `${e.target.value}-${cAno || String(CURRENT_YEAR)}`,
                              };
                              setCuotas(updated);
                            }}
                            style={{ width: "100%" }}
                          >
                            {MONTHS.map((m) => (
                              <option key={m.value} value={m.value}>
                                {m.label}
                              </option>
                            ))}
                          </select>
                          <select
                            className="form-control form-control-sm"
                            value={cAno || String(CURRENT_YEAR)}
                            onChange={(e) => {
                              const updated = [...cuotas];
                              updated[idx] = {
                                ...updated[idx],
                                mes: `${cMes || "01"}-${e.target.value}`,
                              };
                              setCuotas(updated);
                            }}
                            style={{ width: "100%" }}
                          >
                            {[CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1, CURRENT_YEAR + 2].map((y) => (
                              <option key={y} value={String(y)}>
                                {y}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                      <td style={{ width: "30%", textAlign: "right" }}>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={cuota.importe || ""}
                          onChange={(e) => {
                            const updated = [...cuotas];
                            updated[idx] = { ...updated[idx], importe: Number(e.target.value) };
                            setCuotas(updated);
                          }}
                          style={{ width: "100%", textAlign: "right" }}
                        />
                      </td>
                      <td style={{ width: "30%", color: "#6c757d", fontSize: "12px" }}>{cuota.facturanumero || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className={styles.bodyFooter}>
          <Button color="secondary" outline onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};
